import { Injectable } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  DataNoRowOverlayComponent,
  DurationCellRendererComponent,
  FormatValueConversionCellRendererComponent,
  getContextMenuItems,
  TimeAgoCellRendererComponent,
} from '@y42/ag-grid';
import { ListViewColDef } from '@y42/core/list-view';
import { DateTimeService } from '@y42/date-time';
import { EntityTypeCellRendererComponent, EntityTypeCellRendererParams } from '@y42/feature/shared/list-view';
import {
  ImportTypeCellRendererComponent,
  JobStatusCellRendererComponent,
  JobStatusValueFormatter,
} from '@y42/job/ag-grid';
import { ImportWriteType, JobStatus, Y42FeatureType } from '@y42/json-schema';
import { GridOptions, GridReadyEvent, ValueFormatterParams } from 'ag-grid-community';
import { BehaviorSubject, filter, tap, timer } from 'rxjs';
import { OrchestrationJobLogsCellRendererComponent } from '../orchestration-job-logs-cell-renderer/orchestration-job-logs-cell-renderer.component';
import {
  AssetPathCellRendererComponent,
  AssetPathCellRendererParams,
} from './asset-path-cell-renderer/asset-path-cell-renderer.component';

interface OrchestrationRunsJobsListColDefConfig {
  allowAssetRedirection: boolean;
  showLogsButtonColumn?: boolean;
}

export interface OrchestrationRunJobEntity {
  featureType: Y42FeatureType;
  assetPath: string;
  mode: ImportWriteType;
  jobId: string;
  jobStatus: JobStatus;
  createdAt: string;
  finishedAt?: string;
  totalRowCount: number;
  currentRowCount: number;
}

@UntilDestroy()
@Injectable()
export class OrchestrationRunJobsListAgGridConfigService {
  constructor(private readonly dateTimeService: DateTimeService) {
    this.adjustSortingForColumnsWithValuesChangingInTime().pipe(untilDestroyed(this)).subscribe();
  }

  private readonly isGridReady$ = new BehaviorSubject<GridReadyEvent | undefined>(undefined);
  private readonly redirectToAsset$$ = new BehaviorSubject<string | undefined>(undefined);

  readonly redirectToAsset$ = this.redirectToAsset$$.pipe(untilDestroyed(this));

  readonly gridOptions: GridOptions = {
    suppressCellFocus: true,
    suppressAggFuncInHeader: true,
    suppressColumnVirtualisation: true,
    defaultColDef: {
      menuTabs: ['filterMenuTab'],
      sortable: true,
      resizable: true,
      suppressMovable: true,
      suppressMenu: false,
      flex: 1,
    },
    noRowsOverlayComponent: DataNoRowOverlayComponent,
    noRowsOverlayComponentParams: {
      text: 'No Results Found',
      body: 'Please try with another filter',
    },
    rowClass: 'cursor-pointer',
    animateRows: true,
    suppressRowClickSelection: true,
    getContextMenuItems,
  };

  getColumnDefs(config?: OrchestrationRunsJobsListColDefConfig) {
    const baseColumnDefs: Array<ListViewColDef<OrchestrationRunJobEntity>> = [
      {
        headerName: 'Status',
        colId: 'jobStatus',
        field: 'jobStatus',
        minWidth: 60,
        maxWidth: 80,
        cellRenderer: JobStatusCellRendererComponent,
        filter: 'agSetColumnFilter',
        filterParams: {
          valueFormatter: JobStatusValueFormatter,
        },
      },
      {
        headerName: 'Asset Type',
        colId: 'featureType',
        aggFunc: 'first',
        cellRenderer: EntityTypeCellRendererComponent,
        cellRendererParams: {} as EntityTypeCellRendererParams,
        minWidth: 70,
        maxWidth: 80,
        valueGetter: (params) => {
          return params?.data?.featureType || null;
        },
        filter: false,
        sortable: false,
        suppressMenu: true,
      },
      {
        headerName: 'Mode',
        colId: 'mode',
        field: 'mode',
        cellRenderer: ImportTypeCellRendererComponent,
        minWidth: 100,
        sortable: true,
        filter: 'agSetColumnFilter',
        filterParams: {
          valueFormatter: (params: ValueFormatterParams<unknown, ImportWriteType>) => {
            if (params.value === ImportWriteType.Append) {
              return 'Incremental';
            } else {
              return 'Full';
            }
          },
        },
      },
      {
        headerName: 'Asset path',
        colId: 'assetPath',
        cellRenderer: AssetPathCellRendererComponent,
        cellRendererParams: {
          allowAssetRedirection: config?.allowAssetRedirection,
          redirectToAsset: (path: string) => {
            this.redirectToAsset$$.next(path);
          },
        } satisfies AssetPathCellRendererParams,
        valueGetter: (params) => {
          return params?.data?.assetPath || null;
        },
        valueFormatter: (params) => params.value || '',
        minWidth: 250,
        sortable: true,
        filter: 'agTextColumnFilter',
        filterParams: {
          suppressAndOrCondition: true,
        },
        filterValueGetter: (params) => params.data?.assetPath || '',
      },
      {
        headerName: 'Job id',
        colId: 'jobId',
        minWidth: 270,
        valueGetter: (params) => {
          return params?.data?.jobId || null;
        },
        valueFormatter: (params) => params.value || '-',
        sortable: true,
        filter: 'agTextColumnFilter',
        filterParams: {
          suppressAndOrCondition: true,
        },
        filterValueGetter: (params) => params.data?.jobId || '',
      },
      {
        headerName: 'Started',
        colId: 'createdAt',
        field: 'createdAt',
        cellRenderer: TimeAgoCellRendererComponent,
        minWidth: 60,
        sortable: true,
        filter: false,
        suppressMenu: true,
      },
      {
        headerName: 'Duration',
        colId: 'duration',
        minWidth: 60,
        cellRenderer: DurationCellRendererComponent,
        valueGetter: (params) => {
          const job = params.data;
          if (!job) {
            return undefined;
          }

          const createdAt = job.createdAt;
          const finishedAt = job.finishedAt;
          return {
            startedAt: createdAt,
            finishedAt,
          };
        },
        sortable: true,
        comparator: (
          valueA: { startedAt: Date | undefined; finishedAt: Date | undefined },
          valueB: { startedAt: Date | undefined; finishedAt: Date | undefined },
        ) => {
          const calcDiff = (val: { startedAt: Date | undefined; finishedAt: Date | undefined }) => {
            return this.dateTimeService.diff(val.startedAt || new Date(), val.finishedAt || new Date());
          };
          const diffA = calcDiff(valueA);
          const diffB = calcDiff(valueB);

          return diffA - diffB;
        },
        filter: false,
        suppressMenu: true,
      },
      {
        headerName: 'Total Rows',
        colId: 'totalRowCount',
        cellRenderer: FormatValueConversionCellRendererComponent,
        valueGetter: (params) => {
          return params.data?.totalRowCount !== undefined ? { number: params.data.totalRowCount } : undefined;
        },
        minWidth: 60,
        sortable: true,
        comparator: (valueA: { number: number } | undefined, valueB: { number: number } | undefined) => {
          const UNDEFINED_VAL = -1;
          return (valueA?.number ?? UNDEFINED_VAL) - (valueB?.number ?? UNDEFINED_VAL);
        },
        filter: false,
        suppressMenu: true,
      },
      {
        headerName: 'New rows',
        colId: 'currentRowCount',
        cellRenderer: FormatValueConversionCellRendererComponent,
        valueGetter: (params) => {
          return params.data?.currentRowCount !== undefined
            ? { number: params.data.currentRowCount, prefix: '+' }
            : undefined;
        },
        minWidth: 60,
        sortable: true,
        comparator: (valueA: { number: number } | undefined, valueB: { number: number } | undefined) => {
          const UNDEFINED_VAL = -1;
          return (valueA?.number ?? UNDEFINED_VAL) - (valueB?.number ?? UNDEFINED_VAL);
        },
        filter: false,
        suppressMenu: true,
      },
    ];

    const showLogsButtonColumnDef: ListViewColDef<OrchestrationRunJobEntity> = {
      headerName: '',
      minWidth: 130,
      valueGetter: (params) => {
        if (params?.data?.jobId && params?.data?.assetPath) {
          return { jobId: params?.data?.jobId, path: params.data.assetPath };
        }
        return '';
      },
      cellRendererSelector: (params) => {
        if (params?.data?.jobId && params?.data?.assetPath) {
          return { component: OrchestrationJobLogsCellRendererComponent };
        }
        return undefined;
      },
      sortable: false,
      filter: false,
      suppressMenu: true,
    };

    let colDefs = [...baseColumnDefs];

    if (config?.showLogsButtonColumn) {
      colDefs = [...colDefs, showLogsButtonColumnDef];
    }

    return colDefs;
  }

  public gridReady(params: GridReadyEvent) {
    this.gridOptions.api = params.api;
    this.gridOptions.columnApi = params.columnApi;
    this.isGridReady$.next(params);
  }

  private adjustSortingForColumnsWithValuesChangingInTime() {
    return timer(1_000, 1_000).pipe(
      filter(() => {
        if (!this.gridOptions.api || !this.gridOptions.columnApi) {
          return false;
        }
        const columnState = this.gridOptions.columnApi?.getColumnState();
        const isSortSetOnDurationCol = !!columnState.find((col) => col.colId === 'duration')?.sort;

        return isSortSetOnDurationCol;
      }),
      tap(() => this.gridOptions.api?.onSortChanged()),
    );
  }
}
